import { hasValue } from '@core/utils/equality';

import type { FilterByOpts, NamedFilterOpts } from '../../../utils/ApiClient';
import { type DataTablePreservedState, getStateKey } from '../useTablePreservedState';

export type DataTablePreservedStateV4<TRow extends { id: string }> = DataTablePreservedState<TRow>;

export const getV4StateKey = (tableName: string): string =>
  `DataTablePreservedState:v4:${tableName}`;

const LEGACY_READY_MIX_LABEL = 'Mix';
const READY_MIX_LABEL = 'Ready Mix';
const PRODUCT_CATEGORY_FILTER_NAMES: readonly string[] = ['category', 'productCategory'];
const PRODUCT_CATEGORY_TAB_TABLES: readonly string[] = ['products', 'forecasts'];

const renameLegacyValues = <T>(values: T[] | undefined): T[] | undefined =>
  values?.map((value) =>
    value === LEGACY_READY_MIX_LABEL ? (READY_MIX_LABEL as unknown as T) : value,
  );

const isProductCategoryFilter = <TRow extends { id: string }>(
  filter: FilterByOpts<TRow>,
): filter is NamedFilterOpts<TRow> =>
  'name' in filter && PRODUCT_CATEGORY_FILTER_NAMES.includes(String(filter.name));

const renameLegacyFilter = <TRow extends { id: string }>(
  filter: FilterByOpts<TRow>,
): FilterByOpts<TRow> =>
  isProductCategoryFilter(filter)
    ? { ...filter, values: renameLegacyValues(filter.values) ?? [] }
    : filter;

const renameLegacyNamedFilter = <TRow extends { id: string }>(
  filter: NamedFilterOpts<TRow>,
): NamedFilterOpts<TRow> =>
  isProductCategoryFilter(filter)
    ? { ...filter, values: renameLegacyValues(filter.values) ?? [] }
    : filter;

export const transformV4StateToV5 = <TRow extends { id: string }>(
  value: DataTablePreservedStateV4<TRow>,
): DataTablePreservedState<TRow> => ({
  ...value,
  checkboxFilters: value.checkboxFilters?.map(renameLegacyNamedFilter),
  columnFilters: value.columnFilters?.map(renameLegacyFilter),
  activeTabFilterValues:
    hasValue(value.name) && PRODUCT_CATEGORY_TAB_TABLES.includes(value.name)
      ? renameLegacyValues(value.activeTabFilterValues)
      : value.activeTabFilterValues,
});

export const getAndUpgradeV4PreservedState = <TRow extends { id: string }>(
  tableName: string,
): DataTablePreservedState<TRow> => {
  const str = localStorage.getItem(getV4StateKey(tableName));
  if (!hasValue(str)) {
    return {};
  }

  let value: DataTablePreservedStateV4<TRow>;
  try {
    value = JSON.parse(str);
    if (value === null || Array.isArray(value) || typeof value !== 'object') {
      throw new Error();
    }
  } catch {
    localStorage.removeItem(getV4StateKey(tableName));
    return {};
  }

  const upgradedData = transformV4StateToV5(value);
  localStorage.setItem(getStateKey(tableName), JSON.stringify(upgradedData));
  localStorage.removeItem(getV4StateKey(tableName));
  return upgradedData;
};
